// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { List, list } from '../list';
import { prepareUrl } from '../common';

/**
 * @class Jsw.list.Domains
 * @extends Jsw.List
 */
export const Domains = Class.create(List, {
    _initConfiguration($super, config) {
        config = Object.extend({
            searchOveral: 'domainName',
        }, config || {});
        $super(config);

        this._disabledSelectHint = this.lmsg('disabledSelectHint');
        this._urls = this._getConfigParam('urls', {});
        this._icons = this._getConfigParam('icons', {});
        this._userId = this._getConfigParam('userId');

        this._operations = [];
        if (this._urls.createDomain) {
            this._operations.push({
                componentType: 'Jsw.SmallButton',
                id: 'buttonAddDomain',
                cls: 's-btn sb-new-domain',
                title: this.lmsg('buttonAddDomain'),
                description: this.lmsg('hintAddDomain'),
                href: this._urls.createDomain,
            });
        }
        if (this._urls.createSubdomain) {
            this._operations.push({
                componentType: 'Jsw.SmallButton',
                id: 'buttonAddSubDomain',
                cls: 's-btn sb-new-subdomain',
                title: this.lmsg('buttonAddSubdomain'),
                description: this.lmsg('hintAddSubdomain'),
                href: this._urls.createSubdomain,
            });
        }
        if (this._urls.createDomainAliases) {
            this._operations.push({
                componentType: 'Jsw.SmallButton',
                id: 'buttonAddDomainAlias',
                cls: 's-btn sb-new-domain-alias',
                title: this.lmsg('buttonAddDomainAlias'),
                description: this.lmsg('hintAddDomainAlias'),
                href: this._urls.createDomainAliases,
            });
        }
        if (this._operations.length) {
            this._operations.push({
                componentType: 'Jsw.bar.Separator',
            });
        }
        this._operations.push({
            componentType: 'Jsw.list.AdditionalActions',
            title: this.lmsg('buttonChangeStatus'),
            operations: [{
                componentType: 'Jsw.SmallButton',
                id: 'buttonSuspendDomains',
                title: this.lmsg('buttonSuspendDomains'),
                description: this.lmsg('hintSuspendDomains'),
                addCls: 'sb-suspend',
                cls: 'sb-btn',
                handler: function () {
                    this.execGroupOperation({
                        url: this._urls.suspend,
                        skipConfirmation: true,
                    });
                }.bind(this),
            }, {
                componentType: 'Jsw.SmallButton',
                id: 'buttonDisableDomains',
                title: this.lmsg('buttonDisableDomains'),
                description: this.lmsg('hintDisableDomains'),
                addCls: 'sb-turn-off',
                cls: 'sb-btn',
                handler: function () {
                    this.execGroupOperation({
                        url: this._urls.disable,
                        skipConfirmation: true,
                    });
                }.bind(this),
            }, {
                componentType: 'Jsw.SmallButton',
                id: 'buttonActivateDomains',
                title: this.lmsg('buttonActivateDomains'),
                description: this.lmsg('hintActivateDomains'),
                addCls: 'sb-activate',
                cls: 'sb-btn',
                handler: function () {
                    this.execGroupOperation({
                        url: this._urls.activate,
                        skipConfirmation: true,
                    });
                }.bind(this),
            }],
        });
        if (this._urls.remove) {
            this._operations.push({
                componentType: 'Jsw.bar.Separator',
            });
            this._operations.push({
                componentType: 'Jsw.SmallButton',
                id: 'buttonRemoveSite',
                title: this.lmsg('buttonRemove'),
                description: this.lmsg('hintRemove'),
                cls: 's-btn sb-remove-selected',
                handler: function (event) {
                    const homonymSearch = this._getConfigParam('homonymSearch');
                    this.execGroupOperation({
                        url: this._urls.remove,
                        subtype: 'delete',
                        mouseEvent: event,
                        locale: {
                            confirmOnGroupOperation: this.lmsg('removeConfirmation'),
                        },
                        isAjax: !!homonymSearch,
                        requestUrl: homonymSearch && homonymSearch.url,
                        loadingTitle: homonymSearch && homonymSearch.title,
                    });
                }.bind(this),
            });
        }

        this._columns.push(list.COLUMN_SELECTION);
        this._columns.push({
            header: this.lmsg('domainName'),
            sortable: true,
            dataIndex: 'domainDisplayName',
            renderer: function (item) {
                let message = '';
                if (parseInt(item.domainStatus)) {
                    let messageText = this.lmsg('statusDisabled');
                    let messageIcon = this._icons.disabled;
                    let messageAlt = 'disabled';
                    if (item.turnOffAction === 'suspend') {
                        messageText = this.lmsg('statusSuspended');
                        messageIcon = this._icons.suspended;
                        messageAlt = 'suspended';
                    }
                    message += '<div class="b-indent"><span class="b-indent-icon"><span>' +
                        `<span class="tooltipData">${messageText}</span>` +
                        `<img src="${messageIcon}" alt="${messageAlt}" title="">` +
                        '</span>';
                }

                message += (item.overviewUrl ? `<a href="${prepareUrl(item.overviewUrl)}">${item.domainDisplayName.escapeHTML()}</a>` : item.domainDisplayName.escapeHTML());

                if (parseInt(item.domainStatus)) {
                    message += '</span></div>';
                }

                let description = '';
                if (item.adminDescription) {
                    description += '<div class="hint">' +
                        `${item.adminDescription.truncate(50, '...').escapeHTML()}` +
                        '<span class="tooltipData">${item.adminDescription.escapeHTML()}</span>' +
                    '</div>';
                }
                if (item.resellerDescription) {
                    const descriptionTitle = (item.hideResellerTitleDescription) ? '' : `${this.lmsg('resellerDescription')}: `;
                    description += '<div class="hint">'
                        + `${descriptionTitle}${item.resellerDescription.truncate(50, '...').escapeHTML()}` +
                        `<span class="tooltipData">${item.resellerDescription.escapeHTML()}</span></div>`;
                }
                if (item.ownerDescription) {
                    description += `<div class="hint">${this.lmsg('ownerDescription')}: ${item.ownerDescription.truncate(50, '...').escapeHTML()}` +
                        `<span class="tooltipData">${item.ownerDescription.escapeHTML()}</span></div>`;
                }
                message += description;

                return message;
            }.bind(this),
        });
        if (this._getConfigParam('icpPermitColumn')) {
            this._columns.push(this._getConfigParam('icpPermitColumn'));
        }
        this._columns.push({
            header: this.lmsg('hostingType'),
            sortable: true,
            dataIndex: 'hostingType',
            renderer: function (item) {
                let message = '';

                if (item.hostingType === 'alias') {
                    message = `<img src="${this._icons.alias}"/> ${this.lmsg('aliasFor')} `
                        + `<a href="http://${item.realDomainName.escapeHTML()}" target="_blank">${item.realDomainDisplayName.escapeHTML()}</a>`;
                } else if (item.hostingType === 'vrt_hst') {
                    message = `<img src="${this._icons.website}"/>`
                        + ` ${this.lmsg(item.parentDomainId > 0 ? 'subdomainWebsite' : 'website')}`;
                } else if (item.hostingType === 'none') {
                    message = `<img src="${this._icons.none}"/>`
                        + `<em class="hint">${this.lmsg('noHosting')}</em>`;
                } else if (item.hostingType === 'frm_fwd' || item.hostingType === 'std_fwd') {
                    message = `<img src="${this._icons.forwarding}"/>`
                        + ` ${this.lmsg('forwardingTo')} `
                        + `<a href="${item.forwardingUrl.escapeHTML()}" target="_blank">${item.forwardingDisplayUrl.escapeHTML()}</a>`;
                }

                return message;
            }.bind(this),
        });
        if (this._getConfigParam('showOwnerName')) {
            this._columns.push({
                header: this.lmsg('ownerName'),
                sortable: true,
                dataIndex: 'ownerName',
                renderer: function (item) {
                    let linkHref = null;
                    if (parseInt(item.ownerId) !== this._userId) {
                        if ('client' === item.ownerType) {
                            linkHref = 'customer';
                        } else if ('reseller' === item.ownerType) {
                            linkHref = 'reseller';
                        }
                    }
                    return (linkHref ? `<a href="${Jsw.baseUrl}/${linkHref}/overview/id/${item.ownerId}">` : '')
                        + item.ownerName.escapeHTML() +
                        (linkHref ? '</a>' : '') +
                        (item.ownerCompanyName ? `, ${item.ownerCompanyName.escapeHTML()}` : '');
                }.bind(this),
            });
        }
        this._columns.push({
            header: this.lmsg('setupDate'),
            sortable: true,
            dataIndex: 'setupDate',
            renderer(item) {
                return item.setupDateString.escapeHTML();
            },
        });
        if (this._getConfigParam('showExpirationDate')) {
            this._columns.push({
                header: this.lmsg('expirationDate'),
                sortable: false,
                dataIndex: 'expirationDate',
                renderer(item) {
                    if (item.expirationDateString) {
                        return item.expirationDateString.escapeHTML();
                    }
                    return '&mdash;';
                },
            });
        }
        this._columns.push({
            header: this.lmsg('diskUsage'),
            sortable: true,
            dataIndex: 'realSize',
            renderer(item) {
                return item.diskUsage.escapeHTML();
            },
        });
        this._columns.push({
            header: this.lmsg('traffic'),
            sortable: false,
            dataIndex: 'traffic',
        });
        this._columns.push({
            header: '',
            sortable: false,
            renderer: function (item) {
                if (item.hostingType === 'none') {
                    return '';
                }

                return `<a href="http://${item.siteUrl.escapeHTML()}" class="s-btn sb-publish" target="_blank"><span>${this.lmsg('openSite')}</span></a>`;
            }.bind(this),
        });
        this._columns.push({
            header: '',
            sortable: false,
            renderer: function (item) {
                if (!item.previewUrl) {
                    return '';
                }

                return `<a href="${item.previewUrl}" class="s-btn sb-view-site" target="_blank"><span>${this.lmsg('previewSite')}</span></a>`;
            }.bind(this),
        });
        this._columns.push({
            header: '',
            sortable: false,
            renderer: function (item) {
                if (!item.manageUrl || parseInt(item.ownerId) === this._userId) {
                    return '';
                }

                const href = `/admin${item.ownerType === 'reseller' ? '/reseller' : '/customer'}/login/id/${item.ownerId}?pageUrl=${encodeURIComponent(item.manageUrl)}`;

                return `<a href="${href}" class="s-btn sb-login"><span>${
                    this.lmsg(item.ownerType === 'reseller' ? 'manageInResellerPanel' : 'manageInCustomerPanel')
                }</span></a>`;
            }.bind(this),
        });
    },
});



// WEBPACK FOOTER //
// ./jsw/list/domains.js